import {action, observable, computed} from 'mobx';

import urls from 'urls';
import {GCS_PATH_IDS, request_access, upload_file_gcs} from '~/common/storage/gcs_storage';

const DEFAULT_EVENT_CONTEXT = 'invitation';
const DEFAULT_INPUT_NAME = 'source';
const PATH = 'event_video';
const UPLOAD_PROGRESS = 10;
const FUNCTION_A = 101.3;
const FUNCTION_B = 7.9793;
const MAX_VIDEO_SIZE_MB = 300;
const MAX_VIDEO_SIZE = MAX_VIDEO_SIZE_MB * 1024 * 1024;

export class VideoStore {
  @observable accessor videoData = {};

  @observable accessor video_id = null;

  @observable accessor isVideoUploading = false;

  @observable accessor isVideoUploaded = false;

  @observable accessor videoError = false;

  @observable accessor progress = 0;

  @observable accessor previousVersion = 0;

  @observable accessor isEditing = false;

  @observable accessor video_changed = false;

  @observable accessor isBeingCanceled = false;

  @observable accessor customThumbnailURL = false;

  @observable accessor customThumbnailPATH = false;

  @observable accessor isPublished = false;

  @observable accessor permissionWarning = false;

  @observable accessor permissionError = false;

  @observable accessor microphone = 'before_mic';

  @observable accessor photo_library = 'before_pl';

  @observable accessor camera = 'before_cam';

  xhrGCS = new XMLHttpRequest();

  startTime;

  progressTimeout = null;

  fileInput = null;

  constructor(video_id = null) {
    if (video_id) {
      this.video_id = video_id;
    }
  }

  @action
  removeVideo = async (event_id) => {
    if (event_id) {
      await this.delete_video(event_id);
    }
    this.videoData = {};
    this.isPublished = false;
    this.video_id = null;
    this.isVideoUploading = false;
    this.isVideoUploaded = false;
    this.videoError = false;
    this.progress = 0;
    this.previousVersion = 0;
    this.isEditing = false;
    // if it has video_id delete it from server
  };

  @action
  uploadVideo = async (file) => {
    this.startTime = new Date();
    this.isBeingCanceled = false;
    this.isVideoUploading = true;
    this.videoError = false;
    const upload_request = await this.request_upload({
      mimetype: file.type || 'application/octet-stream',
      redirect: true,
    });
    if (this.isBeingCanceled) {
      return;
    }
    const onProgress = (e) => {
      try {
        this.progress = parseInt(100 * (e.loaded / e.total / UPLOAD_PROGRESS));
      } catch (e) {
        console.error(e);
      }
    };
    try {
      await upload_file_gcs(upload_request, file, onProgress, this.xhrGCS);
      if (this.isBeingCanceled) {
        return;
      }
    } catch (err) {
      this.onVideoError(err);
      return;
    }
    this.progress = UPLOAD_PROGRESS;
    await this.renderVideo();
    if (this.isBeingCanceled) {
      return;
    }
    this.video_changed = true;
    this.checkProgress();
  };

  ajaxVideoState = async () => {
    const url = urls.get('ajax_video_state', {video_id: this.video_id});
    const response = await evite.fetch(url, {
      method: 'GET',
    });
    if (response.ok) {
      const json = await response.json();
      return json;
    }
  };

  @action
  checkProgress = async () => {
    const response = await this.ajaxVideoState();
    const {segments} = response;
    let has_errors = false;
    let is_rendered = false;
    if (segments.length) {
      const video = segments.find((segment) => segment.event_context === 'invitation');
      has_errors = video.has_rendering_error;
      if (has_errors) {
        const {rendering_error} = video;
        console.error('error rendering video', rendering_error);
        if (!this.isEditing) {
          this.videoData = {};
          this.video_id = null;
        }
        this.isVideoUploading = false;
        this.isVideoUploaded = false;
        this.progress = 0;
        if (this.progressTimeout) {
          clearInterval(this.progressTimeout);
        }
        this.videoError = rendering_error;
        // clean the error after we show it, so it won't show after we refresh EVT-17227
        const url = urls.get('ajax_video_clean', {
          video_id: this.video_id,
          event_context: DEFAULT_EVENT_CONTEXT,
        });
        fetch(url, {
          method: 'POST',
          body: JSON.stringify({all_but_latest: true}),
          credentials: 'include',
          headers: {
            'X-CSRFToken': evite.cookie('csrftoken'),
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        });
      } else if (
        video &&
        (!this.isEditing ||
          (this.isEditing &&
            video.rendered_video &&
            video.rendered_video.version_number !== this.previousVersion))
      ) {
        // if we are editing the video we need to make sure that the version_number changed

        is_rendered = video.is_rendered;

        if (is_rendered) {
          if (video.rendered_video_preview_image_url) {
            video.rendered_video_preview_image_url += `?${Date.now()}`;
          }
          this.videoData = video;
          this.isPublished = !!this.videoData.published_video;
          clearInterval(this.progressTimeout);
          this.progressTimeout = null;
          this.progress = 100;
          this.isVideoUploading = false;
          this.isVideoUploaded = true;
        } else {
          // this part is in case the rendering was triggered by copying an event EVT-17047
          if (!this.isVideoUploading) {
            this.isVideoUploading = true;
            this.progress = UPLOAD_PROGRESS;
            this.startTime = new Date();
          }
        }
      }
    }

    if (!is_rendered && !has_errors) {
      if (!this.progressTimeout) {
        this.startFakeProgress();
      }

      setTimeout(() => {
        if (this.isBeingCanceled) {
          return;
        }
        this.checkProgress();
      }, 5000);
    }
  };

  startFakeProgress = () => {
    this.progressTimeout = setInterval(() => {
      const currentDate = new Date();
      const secondsPassed = (currentDate - this.startTime) / 1000;
      const newProgress = Math.floor((FUNCTION_A * secondsPassed) / (secondsPassed + FUNCTION_B));
      this.progress = newProgress < 100 ? newProgress : 99;
    }, 1000);
  };

  renderVideo = async () => {
    const url = urls.get('ajax_video_render', {
      video_id: this.video_id,
      event_context: DEFAULT_EVENT_CONTEXT,
    });
    const response = await evite.fetch(url, {
      method: 'POST',
    });
    if (this.isBeingCanceled) {
      return;
    }
    if (response.ok) {
      const json = await response.json();
      return json;
    }
    return Error;
  };

  onVideoError(err) {
    this.videoError = err;
  }

  createAndUpload = async (file) => {
    this.isBeingCanceled = false;
    if (file.size > MAX_VIDEO_SIZE) {
      this.videoError = 'video_too_big';
      return;
    }
    if (this.video_id && this.videoData.rendered_video) {
      // video_id already exists it means is an Edit
      this.previousVersion = this.videoData.rendered_video.version_number;
      this.isEditing = true;
      this.videoData = {};
      this.isVideoUploading = true;
      this.isVideoUploaded = false;
      this.videoError = false;
      this.progress = 0;
      this.uploadVideo(file);
    } else {
      const video_template_id = 'video_dyo';
      const url = urls.get('ajax_video_create', {
        event_context: DEFAULT_EVENT_CONTEXT,
      });
      const response = await evite.fetch(url, {
        method: 'POST',
        body: JSON.stringify({video_template_id}),
        credentials: 'include',
        headers: {
          'X-CSRFToken': evite.cookie('csrftoken'),
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const json = await response.json();
        this.video_id = json.id;
        this.uploadVideo(file);
      } else {
        console.error(`Error while posting to ${url} errorThrown=${error}`);
      }
    }
  };

  request_upload = async ({upload_path = GCS_PATH_IDS.event_video, mimetype, redirect}) => {
    const body = {
      mimetype,
      upload_path,
      redirect,
    };
    const params = {
      video_id: this.video_id,
      event_context: DEFAULT_EVENT_CONTEXT,
      input_name: DEFAULT_INPUT_NAME,
    };
    try {
      const response = await request_access(upload_path, params, body);
      return response;
    } catch (e) {}
  };

  publish = async (event_id) => {
    const url = urls.get('ajax_video_publish', {
      video_id: this.video_id,
      event_id,
      event_context: DEFAULT_EVENT_CONTEXT,
    });
    const response = await evite.fetch(url, {
      method: 'POST',
      body: JSON.stringify({send_event: true}),
    });
    if (response.ok) {
      return response;
    }
  };

  @action
  onCustomThumbnailChange = async (e) => {
    const image = e.target.files[0];
    const path = GCS_PATH_IDS.video_preview_image;
    const params = {
      video_id: this.video_id,
      event_context: DEFAULT_EVENT_CONTEXT,
    };
    const body = {
      redirect: false,
      mimetype: image.type,
    };
    try {
      const upload_request = await request_access(path, params, body);
      const {access_url} = upload_request;
      await upload_file_gcs(upload_request, image);
      return access_url;
    } catch (e) {
      console.error(e);
    }
  };

  @action
  onSaveCustomThumbnail = async (file) => {
    const path = GCS_PATH_IDS.video_preview_image;
    const params = {
      video_id: this.video_id,
      event_context: DEFAULT_EVENT_CONTEXT,
    };
    const body = {
      redirect: false,
      mimetype: file.type,
    };
    try {
      const upload_request = await request_access(path, params, body);
      const {gcs_path, access_url} = upload_request;
      await upload_file_gcs(upload_request, file);
      this.setCustomThumbnailURL(access_url, gcs_path);
    } catch (e) {
      console.error(e);
    }
  };

  @action
  setCustomThumbnailURL(url, gcs_path) {
    this.customThumbnailURL = `${url}?${Date.now()}`;
    this.customThumbnailPATH = gcs_path;
  }

  @computed
  get isCustomThumbnail() {}

  @action
  saveThumbnail = async (gcs_path) => {
    const url =
      urls.get('ajax_video_set_thumbnail', {
        video_id: this.video_id,
        event_context: DEFAULT_EVENT_CONTEXT,
      }) + gcs_path;
    const response = await evite.fetch(url, {
      method: 'POST',
    });
    if (response.ok) {
      // check progress can also be used to update the video info (we need to update the new thumbnail)
      this.checkProgress();
      return response;
    }
  };

  @action
  delete_video = async (event_id) => {
    // delete url gets to queue
    const {video_id} = this;
    // because it takes time to delete the video we also need to detach the video from the event (This is inmediate )
    if (event_id) {
      const detachURL = urls.get('ajax_video_detach', {
        event_id,
        video_id,
      });

      const response = await evite.fetch(detachURL, {
        method: 'POST',
      });
      if (response.ok) {
        this.video_id = null;
        return response;
      }
    } else {
      this.video_id = null;
    }
    const deleteURL = urls.get('ajax_video_delete', {video_id});
    evite.fetch(deleteURL, {
      method: 'DELETE',
    });
  };

  @action
  cancelUpload = async () => {
    this.xhrGCS.abort();
    this.isBeingCanceled = true;
    clearInterval(this.progressTimeout);
    this.progress = 0;
    this.isVideoUploading = false;
    const url = urls.get('ajax_video_clean', {
      video_id: this.video_id,
      event_context: DEFAULT_EVENT_CONTEXT,
    });
    await fetch(url, {
      method: 'POST',
    });
    if (!this.isPublished) {
      const {id} = evite.stores.eventForm.event;
      await this.delete_video(id);
    } else {
      this.isBeingCanceled = false;
      this.previousVersion = 0;
      this.checkProgress();
    }
  };

  @action
  setPermissions = async ({microphone, photo_library, camera}) => {
    this.microphone = microphone;
    this.photo_library = photo_library;
    this.camera = camera;
    const notYetAsk = microphone === 'notDetermined' || camera === 'notDetermined';
    // if any of the permissions is still not determined it means we haven't ask the permissions, so we shouldn't show any errors or warnings
    if (!notYetAsk) {
      if (photo_library === 'denied') {
        if (microphone === 'denied' || camera === 'denied') {
          this.permissionError = true;
          // error state they can't upload any video
        } else {
          // just warning, they can record but not upload from library
          this.permissionWarning = 'Uploading a video? Allow access to Photos.';
        }
      } else if (microphone === 'denied' || camera === 'denied') {
        // just warning, they can upload from library but not record
        this.permissionWarning = 'Recording a video? Allow access to Camera and Microphone.';
      }
    }
  };
}
